import React, { useEffect, useState } from "react";
import { AppShell } from "@netiv/ui-core";
import { ROLES, getRole } from "./roles";
import { SCREENS, getScreen } from "./screens";
import { SuiteSwitcher } from "./components/SuiteSwitcher";

const API_BASE = import.meta.env.VITE_API_GATEWAY_URL
  ?? import.meta.env.VITE_QUERY_API_URL
  ?? "";

function assertNavigationRegistry() {
  for (const role of ROLES) {
    const leaves = role.nav.flatMap((item) => item.children?.length ? item.children : [item]);
    for (const item of leaves) {
      if (!SCREENS[role.id]?.[item.id]) {
        throw new Error(`Unregistered NETIV screen: ${role.id}/${item.id}`);
      }
    }
  }
}
assertNavigationRegistry();

type SessionContext = {
  organization_id: string;
};

const ROLE_SUBTITLE: Record<string, string> = {
  executive: "Decisions, Value and Compliance",
  business: "Commercial and Treasury Operations",
  compliance: "Governance, Regulatory and Security",
  maintenance: "Platform Configuration and Health",
};

function groupForLegacyRole(role: string): string {
  if (["exec", "analyst"].includes(role)) return "executive";
  if (["lng", "supply", "proc", "ops"].includes(role)) return "business";
  if (["gov", "audit"].includes(role)) return "compliance";
  return ROLES.some((group) => group.id === role) ? role : "executive";
}

export default function App() {
  const [roleId, setRoleId] = useState("executive");
  const [screenId, setScreenId] = useState(getRole("executive").defaultScreen);
  const [session, setSession] = useState<SessionContext | null>(null);
  const [sessionError, setSessionError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API_BASE}/session`, {
      signal: controller.signal,
      credentials: "include",
    })
      .then((response) => {
        if (response.status === 401) {
          window.location.replace(`${API_BASE.replace(/\/$/, "")}/auth/signin`);
          return Promise.reject(new DOMException("Redirecting to sign-in", "AbortError"));
        }
        if (!response.ok) throw new Error(`Session API returned ${response.status}`);
        return response.json();
      })
      .then((value: SessionContext) => {
        setSession(value);
        sessionStorage.setItem("netiv:tenant-id", value.organization_id);
        setRoleId("executive");
        setScreenId(getRole("executive").defaultScreen);
      })
      .catch((reason) => {
        if (reason.name !== "AbortError") setSessionError(reason.message);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    function navigate(event: Event) {
      const detail = (event as CustomEvent<{ roleId: string; screenId: string }>).detail;
      if (!detail) return;
      const nextRole = groupForLegacyRole(detail.roleId);
      setRoleId(nextRole);
      setScreenId(detail.screenId);
    }
    window.addEventListener("netiv:navigate", navigate);
    return () => window.removeEventListener("netiv:navigate", navigate);
  }, []);

  function handleRoleChange(id: string) {
    const nextRole = groupForLegacyRole(id);
    setRoleId(nextRole);
    setScreenId(getRole(nextRole).defaultScreen);
  }

  const role = getRole(roleId);
  const Screen = getScreen(roleId, screenId);

  if (sessionError) {
    return <main className="netiv-session-state" role="alert">Unable to load NETIV.</main>;
  }
  if (!session) {
    return <main className="netiv-session-state" aria-busy="true" />;
  }

  return (
    <AppShell
      brandSubtitle={ROLE_SUBTITLE[roleId] ?? "Operational Intelligence"}
      headerExtra={<SuiteSwitcher />}
      ticker={[]}
      showDaoBadge={roleId === "compliance"}
      roles={ROLES}
      activeRoleId={roleId}
      onRoleChange={handleRoleChange}
      navItems={role.nav}
      activeNavId={screenId}
      onNavChange={setScreenId}
    >
      <Screen />
    </AppShell>
  );
}
